import { useId } from "react";
import type { CanvasPrompt } from "./prompts";

interface QuestionFieldProps {
  /** Accessible name, e.g. "Question for Box 1". */
  label: string;
  value: string;
  prompt: CanvasPrompt;
  maxLength: number;
  /** False when the label is already shown beside the field. */
  showLabel?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  /** Ctrl/Cmd+Enter, for fields that can be sent on their own. */
  onSubmit?: () => void;
}

/**
 * A free-text question with a two-line prompt shown until the user types.
 *
 * The prompt is a layer behind a transparent textarea rather than the
 * placeholder attribute, because a placeholder cannot give the label and the
 * example different weights. It is linked as the field's description, so a
 * screen reader announces it the way it would a placeholder.
 */
export function QuestionField({
  label,
  value,
  prompt,
  maxLength,
  showLabel = true,
  disabled = false,
  onChange,
  onSubmit,
}: QuestionFieldProps) {
  const hintId = useId();
  const empty = value === "";
  return (
    <div className="question-field">
      {empty && (
        <div className="question-hint" id={hintId}>
          {showLabel && <strong>{prompt.label}</strong>}
          <span>{prompt.example}</span>
        </div>
      )}
      <textarea
        aria-label={label}
        aria-describedby={empty ? hintId : undefined}
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (onSubmit && event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            onSubmit();
          }
        }}
      />
    </div>
  );
}
